import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const AccountDeletionPage: React.FC = () => {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [phase, setPhase] = useState<'warning' | 'confirmation'>('warning');

  const handleProceed = () => {
    setPhase('confirmation');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'EXCLUIR') {
      setError('Por favor, digite "EXCLUIR" para confirmar');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { success, error } = await deleteAccount();
      
      if (success) {
        setSuccess(true);
        // Redirecionamento automático após exclusão bem-sucedida
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(error || 'Ocorreu um erro ao excluir sua conta. Tente novamente ou entre em contato com o suporte.');
      }
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Exclusão de Conta - Boardverso" 
        description="Exclua sua conta e dados pessoais do Boardverso"
      />
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex mb-6 items-center">
          <Link to="/perfil" className="text-indigo-600 hover:text-indigo-800 flex items-center">
            <ArrowLeft size={18} className="mr-1" />
            Voltar ao perfil
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-2xl font-bold mb-6 text-gray-800">Exclusão de Conta</h1>

          {success ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <div>
                  <h3 className="text-green-800 font-medium">Conta excluída com sucesso</h3>
                  <p className="mt-1 text-green-700">
                    Sua conta e dados pessoais foram excluídos do Boardverso. Redirecionando para a página inicial...
                  </p>
                </div>
              </div>
            </div>
          ) : phase === 'warning' ? (
            <div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                  <div>
                    <h3 className="text-yellow-800 font-medium">Atenção: esta ação é irreversível</h3>
                    <p className="mt-1 text-yellow-700">
                      Você está prestes a excluir sua conta do Boardverso. Esta ação não pode ser desfeita.
                    </p>
                  </div>
                </div>
              </div>
              
              <h2 className="text-xl font-semibold mb-3">O que acontecerá quando sua conta for excluída:</h2>
              
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Todos os seus dados pessoais serão permanentemente removidos do nosso sistema, incluindo seu perfil, e-mail e preferências.</li>
                <li>Sua coleção, lista de desejos e jogos jogados serão removidos.</li>
                <li>Suas avaliações, comentários e conteúdos publicados serão removidos ou anonimizados.</li>
                <li>Anúncios no mercado serão removidos.</li>
                <li>Mensagens enviadas para outros usuários não podem ser removidas dos dispositivos deles, mas serão excluídas do nosso sistema.</li>
                <li>Você não conseguirá recuperar sua conta ou dados após a conclusão deste processo.</li>
              </ul>

              <button
                onClick={handleProceed}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Entendi e desejo prosseguir
              </button>
            </div>
          ) : (
            <div>
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  <div>
                    <h3 className="text-red-800 font-medium">Confirmação final</h3>
                    <p className="mt-1 text-red-700">
                      Para confirmar a exclusão da sua conta, digite EXCLUIR no campo abaixo.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <div className="mb-6">
                <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                  Digite "EXCLUIR" para confirmar:
                </label>
                <input
                  type="text"
                  id="confirm"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="EXCLUIR"
                />
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  {loading ? 'Processando...' : 'Excluir minha conta permanentemente'}
                </button>
                
                <button
                  onClick={() => setPhase('warning')}
                  disabled={loading}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded disabled:opacity-50"
                >
                  Voltar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountDeletionPage; 